// ── BCH Live — Viewer-facing Diagnostics ──────────────────────────
//
// Turns the raw signals the player already tracks into a plain-English
// verdict about WHY a stream is dropping, and — crucially — WHOSE
// problem it is. The recurring support question is "is it the app or my
// setup?", so every verdict is explicitly attributed:
//
//   • host   — the creator's broadcast software or streaming server
//   • viewer — this device / network / browser
//   • relays — BCH Nostr's own signalling layer (rare)
//   • ok     — nothing wrong, or simply offline
//
// It reads ONLY props — no queries of its own — so it can't itself
// become a source of the very instability it's meant to explain.

import { useEffect, useState } from 'react';
import {
  Activity, ShieldAlert, Wifi, WifiOff, ServerCrash, RadioTower,
  CheckCircle2, AlertTriangle, Clapperboard, X, Copy, Check,
} from 'lucide-react';
import type { HlsDiagnostics } from './LivePlayer';
import type { PlatformInfo } from '../../lib/streamCredentials';
import { HEARTBEAT_TIMEOUT_SECS } from '../../lib/streamCredentials';

// ── Verdict model ──────────────────────────────────────────────────

type Culprit = 'host' | 'viewer' | 'relays' | 'ok';

interface Verdict {
  culprit: Culprit;
  /** Short headline, e.g. "Your stream server is blocking playback". */
  title: string;
  /** One or two sentences the user can act on. */
  detail: string;
  /** Who needs to fix it, in one phrase. */
  fixedBy: string;
}

export interface StreamDiagnosticsInput {
  isLive: boolean;
  isHls: boolean;
  platform: PlatformInfo;
  /** Heartbeat age in seconds (Infinity when never seen). */
  heartbeatAge: number;
  hls: HlsDiagnostics;
  connection: { connectedCount: number; total: number; status: 'good' | 'degraded' | 'poor' };
  /** True when the viewer is the creator — tweaks the guidance wording. */
  isOwner: boolean;
}

// ── Verdict logic ──────────────────────────────────────────────────
//
// Ordered by confidence: the most unambiguous, host-attributable signals
// are checked first so a clear "it's the server" isn't masked by a vaguer
// "reconnecting" reading.

export function diagnose(input: StreamDiagnosticsInput): Verdict {
  const { isLive, isHls, platform, heartbeatAge, hls, connection, isOwner } = input;
  const you = isOwner ? 'your' : "the creator's";
  const You = isOwner ? 'Your' : "The creator's";

  // 1. Not live — there's nothing to drop.
  if (!isLive) {
    return {
      culprit: 'ok',
      title: 'Stream is offline',
      detail: 'This channel isn\'t broadcasting right now, so there\'s nothing to play. Drops can only happen while a stream is live.',
      fixedBy: 'Nothing to fix',
    };
  }

  // 2. CORS block — the single most common "why won't it play" cause, and
  //    unambiguously the host's server config. Applies to HLS only.
  if (hls.corsBlocked) {
    return {
      culprit: 'host',
      title: `${You} streaming server is blocking playback`,
      detail: `The video server isn't sending the "Access-Control-Allow-Origin" header, so the browser refuses to play it here. ${isOwner ? 'Enable CORS on your origin/CDN (allow this site, or "*").' : 'Only the stream host can fix this on their server.'} No amount of reconnecting will help until that's changed.`,
      fixedBy: isOwner ? 'You (server CORS config)' : 'The stream host',
    };
  }

  // 3. Stale heartbeat — the creator's broadcaster stopped publishing. The
  //    stream event says "live" but the beat is older than the tolerance,
  //    which means OBS/their encoder disconnected or their machine slept.
  if (heartbeatAge > HEARTBEAT_TIMEOUT_SECS) {
    return {
      culprit: 'host',
      title: `${You} broadcaster stopped sending updates`,
      detail: `We haven't heard a heartbeat for ${fmtAge(heartbeatAge)} (limit ${HEARTBEAT_TIMEOUT_SECS}s). That points at the broadcasting software — ${isOwner ? 'check OBS/your encoder is still connected and your machine didn\'t sleep.' : 'their OBS/encoder likely dropped or their machine slept.'}`,
      fixedBy: isOwner ? 'You (restart your broadcaster)' : 'The stream host',
    };
  }

  // 4. HTTP 4xx/5xx on segments — the origin is returning errors for media
  //    the player is asking for. Host-side (404 = segments not written,
  //    5xx = server erroring).
  if (isHls && hls.lastErrorHttp && hls.lastErrorHttp >= 400) {
    const code = hls.lastErrorHttp;
    return {
      culprit: 'host',
      title: `${You} server returned HTTP ${code}`,
      detail: code >= 500
        ? `The streaming server is erroring (HTTP ${code}). ${isOwner ? 'Your origin/CDN is unhealthy — check the server.' : 'The host\'s server is having trouble.'}`
        : `The player asked for video segments and got HTTP ${code} — usually the encoder isn't writing segments fast enough, or the stream path is wrong. ${isOwner ? 'Check your encoder is running and the ingest URL matches.' : 'This is on the host\'s side.'}`,
      fixedBy: isOwner ? 'You (encoder / server)' : 'The stream host',
    };
  }

  // 5. Repeated segment retries while nominally live — the video pipe is
  //    unstable. Most often the host's upload bandwidth, but on the viewer
  //    side a weak connection produces the same symptom, so we hedge toward
  //    the host only when segments have never loaded cleanly.
  if (isHls && (hls.rebuildCount > 0 || hls.retryCount >= 3)) {
    const neverPlayed = hls.lastFragOkAt === 0;
    if (neverPlayed) {
      return {
        culprit: 'host',
        title: 'Video never started loading',
        detail: `The player has retried ${hls.retryCount + hls.rebuildCount * 20}+ times without loading a single segment. The stream URL is reachable but no video is coming through — ${isOwner ? 'check your encoder is actually pushing to this URL.' : 'the host\'s encoder likely isn\'t pushing video.'}`,
        fixedBy: isOwner ? 'You (encoder)' : 'The stream host',
      };
    }
    return {
      culprit: 'host',
      title: 'Video keeps buffering and recovering',
      detail: `Playback started but segments keep failing (${hls.retryCount} recent retries${hls.rebuildCount ? `, ${hls.rebuildCount} full rebuilds` : ''}). This is almost always unstable upload bandwidth on the broadcaster's side. The app is auto-recovering — it won't give up. If it's you, a wired connection or lower bitrate usually fixes it.`,
      fixedBy: isOwner ? 'You (upload bandwidth / bitrate)' : 'The stream host',
    };
  }

  // 6. Relays are the app's own layer. If the viewer can't reach them, the
  //    "live/offline" signalling degrades — that's genuinely our side.
  if (connection.status === 'poor') {
    return {
      culprit: 'relays',
      title: 'Can\'t reach the signalling relays',
      detail: `Connected to ${connection.connectedCount}/${connection.total} relays. This affects live status and chat, not the video itself, and usually clears on its own. If it persists, your network may be blocking WebSocket connections.`,
      fixedBy: 'Auto-recovering / your network',
    };
  }

  // 7. Stalled right now but with a healthy history — transient, and the
  //    player is already handling it.
  if (isHls && hls.state === 'stalled') {
    return {
      culprit: 'viewer',
      title: 'Buffering…',
      detail: 'Playback paused to rebuild its buffer. This is usually a brief dip on this device\'s connection. The player reconnects automatically — no action needed.',
      fixedBy: 'Auto-recovering',
    };
  }

  // 8. A provider embed (Twitch/YouTube/etc). We can't see inside a
  //    cross-origin iframe, so we can only state the boundary honestly.
  if (!isHls && platform.platform !== 'unknown' && platform.platform !== 'hls') {
    return {
      culprit: 'ok',
      title: `Playing via ${platform.label}`,
      detail: `This stream is embedded from ${platform.label}. Playback and quality are controlled entirely by ${platform.label} inside their player — if it drops, it's happening on ${platform.label}'s or ${you} side, not here.`,
      fixedBy: `${platform.label} / the stream host`,
    };
  }

  // 9. Everything looks healthy.
  return {
    culprit: 'ok',
    title: 'Playback looks healthy',
    detail: hls.lastFragOkAt
      ? 'Video segments are loading normally and no errors are pending. If you\'re still seeing drops, they\'re brief network dips on this device.'
      : 'No problems detected. If the video is blank, give it a few seconds to connect.',
    fixedBy: 'Nothing to fix',
  };
}

// ── Formatting helpers ─────────────────────────────────────────────

function fmtAge(secs: number): string {
  if (!Number.isFinite(secs)) return 'a while';
  if (secs < 60) return `${Math.round(secs)}s`;
  return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
}

const CULPRIT_STYLE: Record<Culprit, { icon: typeof Activity; ring: string; text: string; chip: string; label: string }> = {
  host:   { icon: ServerCrash,  ring: 'border-red-500/30 bg-red-500/5',       text: 'text-red-500',    chip: 'bg-red-500/15 text-red-500',       label: 'Stream host' },
  viewer: { icon: Wifi,         ring: 'border-amber-500/30 bg-amber-500/5',   text: 'text-amber-500',  chip: 'bg-amber-500/15 text-amber-600',   label: 'This device' },
  relays: { icon: RadioTower,   ring: 'border-amber-500/30 bg-amber-500/5',   text: 'text-amber-500',  chip: 'bg-amber-500/15 text-amber-600',   label: 'App relays' },
  ok:     { icon: CheckCircle2, ring: 'border-emerald-500/30 bg-emerald-500/5', text: 'text-emerald-500', chip: 'bg-emerald-500/15 text-emerald-600', label: 'Healthy' },
};

// ── Panel ──────────────────────────────────────────────────────────

export default function StreamDiagnostics({
  input, onClose,
}: { input: StreamDiagnosticsInput; onClose: () => void }) {
  // Re-render on a clock so heartbeat age and "seconds since last segment"
  // stay live while the panel is open.
  const [, tick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(i);
  }, []);

  const verdict = diagnose(input);
  const style = CULPRIT_STYLE[verdict.culprit];
  const Icon = style.icon;

  const lastSegAgo = input.hls.lastFragOkAt
    ? `${Math.round((Date.now() - input.hls.lastFragOkAt) / 1000)}s ago`
    : '—';

  const [copied, setCopied] = useState(false);
  const copyReport = () => {
    const report = [
      'BCH Live — stream diagnostics',
      `verdict: ${verdict.culprit} — ${verdict.title}`,
      `live: ${input.isLive}  owner: ${input.isOwner}`,
      `platform: ${input.platform.label} (${input.platform.platform})  engine: ${input.hls.engine ?? 'embed'}`,
      `heartbeat age: ${fmtAge(input.heartbeatAge)} (limit ${HEARTBEAT_TIMEOUT_SECS}s)`,
      `hls state: ${input.hls.state}  lastSegment: ${lastSegAgo}`,
      `retries: ${input.hls.retryCount}  rebuilds: ${input.hls.rebuildCount}  cors: ${input.hls.corsBlocked}`,
      `lastError: ${input.hls.lastError ?? 'none'}${input.hls.lastErrorHttp ? ` http=${input.hls.lastErrorHttp}` : ''}`,
      `relays: ${input.connection.connectedCount}/${input.connection.total} (${input.connection.status})`,
    ].join('\n');
    navigator.clipboard?.writeText(report).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="sc-modal-enter relative w-full max-w-md overflow-hidden rounded-t-3xl border border-border bg-card shadow-2xl sm:rounded-3xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="flex items-center gap-2 text-sm font-extrabold">
            <Activity className="h-4 w-4 text-primary" /> Stream diagnostics
          </h2>
          <button onClick={onClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="safe-area-bottom space-y-4 p-5">
          {/* Verdict card */}
          <div className={`rounded-2xl border p-4 ${style.ring}`}>
            <div className="flex items-start gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${style.chip}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="mb-1 flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${style.chip}`}>
                    {style.label}
                  </span>
                </div>
                <p className={`text-sm font-extrabold ${style.text}`}>{verdict.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{verdict.detail}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-background/60 px-3 py-2 text-[11px] font-semibold">
              {verdict.culprit === 'ok'
                ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
              <span className="text-muted-foreground">Fixed by:</span>
              <span className="text-foreground">{verdict.fixedBy}</span>
            </div>
          </div>

          {/* Raw signals — the receipts behind the verdict */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Live signals</p>
            <div className="grid grid-cols-2 gap-1.5">
              <Signal
                icon={input.isLive ? Clapperboard : WifiOff}
                label="Status"
                value={input.isLive ? 'Live' : 'Offline'}
                tone={input.isLive ? 'ok' : 'muted'}
              />
              <Signal
                icon={Activity}
                label="Heartbeat"
                value={input.isLive ? `${fmtAge(input.heartbeatAge)} ago` : '—'}
                tone={input.heartbeatAge > HEARTBEAT_TIMEOUT_SECS && input.isLive ? 'bad' : 'ok'}
              />
              <Signal
                icon={Clapperboard}
                label="Source"
                value={input.hls.engine === 'hls.js' || input.hls.engine === 'native' ? `HLS (${input.hls.engine})` : input.platform.label}
                tone="muted"
              />
              {input.isHls && (
                <Signal
                  icon={Activity}
                  label="Last segment"
                  value={lastSegAgo}
                  tone={input.hls.lastFragOkAt && Date.now() - input.hls.lastFragOkAt < 15000 ? 'ok' : input.hls.lastFragOkAt ? 'bad' : 'muted'}
                />
              )}
              {input.isHls && (
                <Signal
                  icon={ShieldAlert}
                  label="Retries / rebuilds"
                  value={`${input.hls.retryCount} / ${input.hls.rebuildCount}`}
                  tone={input.hls.retryCount || input.hls.rebuildCount ? 'bad' : 'ok'}
                />
              )}
              <Signal
                icon={input.connection.status === 'poor' ? WifiOff : Wifi}
                label="Relays"
                value={`${input.connection.connectedCount}/${input.connection.total}`}
                tone={input.connection.status === 'good' ? 'ok' : input.connection.status === 'poor' ? 'bad' : 'muted'}
              />
            </div>
            {input.hls.lastError && (
              <p className="pt-1 font-mono text-[10px] leading-relaxed text-muted-foreground/80">
                last error: {input.hls.lastError}
                {input.hls.lastErrorHttp ? ` (http ${input.hls.lastErrorHttp})` : ''}
              </p>
            )}
          </div>

          {/* Copy report — for sharing with support */}
          <button
            onClick={copyReport}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-border py-2.5 text-xs font-bold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied report' : 'Copy diagnostic report'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Signal chip ────────────────────────────────────────────────────

function Signal({
  icon: Icon, label, value, tone,
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  tone: 'ok' | 'bad' | 'muted';
}) {
  const toneClass =
    tone === 'ok' ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'bad' ? 'text-red-500'
    : 'text-foreground';
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-background/40 px-2.5 py-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`truncate text-xs font-bold ${toneClass}`}>{value}</p>
      </div>
    </div>
  );
}
